/* ============================================================
   SAM-198 — Pricing section, editor v2 (mock 3 v2, approved 2026-10-01).

   Pricing is the ONLY place with prices. Laid out top to bottom:
     1. How is it charged? — Per <resource> (SAM-591 unit pricing) / Per
        guest (flat + SAM-588 rate groups) / Per event size (SAM-592).
        Drives `pricing.price_unit` through updatePriceUnit, as before.
     2. Currency, once — written to every guest type and add-on (each
        still stores its own `currency`, the payload shape is unchanged).
     3. Seasons as tabs (SAM-589): "All year" edits the base prices; a
        season tab edits that season's overrides in the SAME price table
        (empty = base price), plus its name and dates.
     4. The price table for the model:
        - per unit: unit price / included / max guests, extra guest price
          per guest type, optional "Different price per <resource>"
          (the per-unit overrides that used to live under Units).
        - per guest: one row per rate group (name, who qualifies, price per
          guest type, max per booking / per day) under a "Default rate" row
          (the flat price).
        - per event size: the band matrix.
     5. Guest types (booking options): names, ids, min / max quantity.
     6. Advanced (collapsed): the older role prices and guest groups.

   All state, validation and the save payload still come from
   useServiceEditor; nothing about `booking_options`, `pricing.rate_groups`,
   `pricing.seasons`, `pricing.unit` or the role matrix changes on the wire.
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { CalendarRange, ChevronDown, CreditCard, Plus, Repeat, Tag, Users, X } from 'lucide-react';
import { CURRENCY_OPTIONS } from '../../../hooks/useServiceEditor';
import { ServicePricingMatrix } from '../../../components/ServicePricingMatrix';
import { MonthDayPicker, ServiceSeasonsEditor, recurringHints } from '../../../components/ServiceSeasonsEditor';
import { useUserTypes } from '../../../hooks/useUserTypes';
import { ServiceEventPricingMatrix } from '../../../components/ServiceEventPricingMatrix';
import type { PriceUnit } from '../../../hooks/useServices';
import { ServiceTierGroupsEditor } from '../../../components/ServiceTierGroupsEditor';
import { Notice } from '../../../components/ui/Notice';
import { describeDayRange } from '../../../utils/pricingSeasons';
import { Checkbox } from '../../../components/ui/Checkbox';
import {
  ELIGIBILITY_LABELS, parseRoles, rateGroupIssues, slugifyGroupId, toggleRole, type RateGroupEligibilityType,
} from '../../../utils/rateGroups';
import { resolveUnitConfig, quoteUnit } from '../unitPricing';
import { SectionCard, type Editor } from '../ServiceEditorPage';
import {
  capitalize, clearUnitOverrides, hasMixedCurrency, hasUnitOverrides, pricingCurrency, resourceWord,
  seasonTabLabel, withCurrency,
} from '../editorV2';
import { CURRENCY_SYMBOL } from '../../../utils/money';
import { DateField } from '../../../components/ui/DateTimeField';

const SYMBOL = CURRENCY_SYMBOL;
const money = 'h-10 w-full min-w-[84px] rounded-[8px] border border-solid border-[var(--line)] bg-white px-2 text-[14px]';
const text = 'h-10 w-full rounded-[8px] border border-solid border-[var(--line)] bg-white px-2 text-[14px]';
const th = 'text-left text-[12px] font-semibold px-2 py-2 whitespace-nowrap';
const td = 'px-2 py-1.5 align-top';

function ModelCard({ on, title, example, onClick }: { on: boolean; title: string; example: string; onClick: () => void }) {
  return (
    <button
      type="button" role="radio" aria-checked={on} onClick={onClick}
      className="flex flex-col items-start gap-1.5 text-left p-3.5 rounded-[12px] cursor-pointer min-h-[44px]"
      style={{
        background: on ? 'color-mix(in srgb, var(--tenant-primary, #005488) 8%, white)' : '#fff',
        border: on ? '2px solid var(--tenant-primary, #005488)' : '1px solid var(--line)',
        color: 'var(--ink-900)',
      }}
    >
      <span className="flex items-center gap-2 text-[14px] font-semibold">
        <span
          aria-hidden className="inline-block rounded-full shrink-0"
          style={{ width: 16, height: 16, border: on ? '5px solid var(--tenant-primary, #005488)' : '2px solid var(--ink-300, #b3b3b3)' }}
        />
        {title}
      </span>
      <span className="text-[12px]" style={{ color: 'var(--ink-500)' }}>{example}</span>
    </button>
  );
}

export function PricingSection({ editor }: { editor: Editor }) {
  const {
    formData, setFormData, addBookingOption, removeBookingOption, updateBookingOption,
    invalidPriceIndexes, activeRoleIds, updateOptionRolePrice, addRoleColumn, removeRoleColumn,
    updateDefaultUserTypeId, updateRoleCap, addTierGroup, removeTierGroup, updateTierGroup, toggleTierGroupOption,
    addRateGroup, removeRateGroup, updateRateGroup, updateOptionGroupPrice,
    addSeason, removeSeason, updateSeason, updateSeasonEveryYear, updateSeasonPrice, seasonErrors,
    updateField, updateResource, unitIssues, isEventForm, updateOptionEventPrice, updatePriceUnit, unitCarryOver,
  } = editor;
  const { userTypes } = useUserTypes();
  const unitMode = formData.price_unit === 'unit';
  const eventMode = isEventForm;
  const personMode = !unitMode && !eventMode;
  const word = resourceWord(formData.subtype);
  const currency = pricingCurrency(formData);
  const sym = SYMBOL[currency] || currency;
  const perDay = formData.booking_type === 'day' ? 'per day' : 'per booking';

  // --- Season tabs: -1 = "All year" (base prices) ---
  const [tab, setTab] = useState(-1);
  useEffect(() => { if (tab >= formData.seasons.length) setTab(formData.seasons.length - 1); }, [tab, formData.seasons.length]);
  const season = tab >= 0 ? formData.seasons[tab] : null;
  const seasonEntry = (optionId: string) => season?.prices[optionId];
  const onAddSeason = () => { addSeason(); setTab(formData.seasons.length); };

  const options = formData.booking_options;
  const optLabel = (o: (typeof options)[number], i: number) => o.label_en || o.label_es || o.id || `Guest type ${i + 1}`;
  const groups = formData.rate_groups;
  const groupIssues = rateGroupIssues(groups);

  const [perUnitOpen, setPerUnitOpen] = useState(() => hasUnitOverrides(formData.resources));
  const togglePerUnit = (on: boolean) => {
    setPerUnitOpen(on);
    if (!on) setFormData((prev) => ({ ...prev, resources: clearUnitOverrides(prev.resources) }));
  };

  const addGuestType = () => {
    addBookingOption();
    setFormData((prev) => ({
      ...prev,
      booking_options: prev.booking_options.map((o, i) => (i === prev.booking_options.length - 1 ? { ...o, currency } : o)),
    }));
  };

  const hiddenPersonData = !personMode && (groups.length > 0 || activeRoleIds.length > 0 || formData.tier_groups.length > 0);
  const previewUnit = resolveUnitConfig(formData, formData.resources[0]);
  const previewGuests = Object.fromEntries(options.map((o) => [o.id, Number(o.min_amount) > 0 ? Number(o.min_amount) : 0]));
  const previewQuote = previewUnit && unitMode
    ? quoteUnit(previewUnit, options, { ...previewGuests, [options[0]?.id]: (previewUnit.max_pax ?? previewUnit.included_pax + 1) })
    : null;

  /** A season-aware price input: base value on "All year", the season override (placeholder = base) on a season tab. */
  const priceInput = ({
    label, base, seasonValue, onBase, onSeason, invalid,
  }: {
    label: string; base: string; seasonValue: string | undefined;
    onBase: (v: string) => void; onSeason: (v: string) => void; invalid?: boolean;
  }) => (
    <input
      type="number" min={0} step="0.01" aria-label={label}
      className={money}
      style={invalid && !season ? { borderColor: 'var(--danger)' } : undefined}
      value={season ? (seasonValue ?? '') : base}
      placeholder={season ? (base || '—') : '0'}
      onChange={(e) => (season ? onSeason(e.target.value) : onBase(e.target.value))}
    />
  );

  return (
    <>
      <SectionCard title="How is it charged?" icon={<CreditCard size={14} />}>
        <div role="radiogroup" aria-label="How is it charged?" className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
          <ModelCard
            on={unitMode} title={`Per ${word.singular}`} onClick={() => updatePriceUnit('unit' as PriceUnit)}
            example={`One price per ${word.singular}, guests included; extra guests priced per type.`}
          />
          <ModelCard
            on={personMode} title="Per guest" onClick={() => updatePriceUnit('person' as PriceUnit)}
            example="Each guest type pays its own price, by rate group."
          />
          <ModelCard
            on={eventMode} title="Per event size" onClick={() => updatePriceUnit('event' as PriceUnit)}
            example="One flat price by the size of the event (S / M / L / XL)."
          />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-[13px] font-semibold" htmlFor="pricing-currency" style={{ color: 'var(--ink-700)' }}>Currency</label>
          <select
            id="pricing-currency" className="h-10 rounded-[8px] border border-solid border-[var(--line)] bg-white px-2 text-[14px]"
            value={currency} onChange={(e) => setFormData((prev) => withCurrency(prev, e.target.value))}
          >
            {CURRENCY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.id} · {SYMBOL[o.id] || o.id}</option>)}
          </select>
          {hasMixedCurrency(formData) && (
            <span className="text-[12px]" style={{ color: 'var(--warn-fg)' }}>
              Guest types or add-ons use different currencies; picking one applies it to all.
            </span>
          )}
        </div>
        {unitMode && unitCarryOver && (
          <p className="text-[12px] mt-3" style={{ color: 'var(--ink-500)' }}>
            “{unitCarryOver}” is now the {word.singular} price and the other guest types charge their old price
            per extra guest. Check the included guests before saving.
          </p>
        )}
        {hiddenPersonData && (
          <Notice variant="warn" className="mt-3">
            This service still has rate groups, role prices or guest groups from per-guest pricing. They are kept
            (switching back restores them) but not used by this model.
          </Notice>
        )}
        {formData.subtype === 'minconsumo' && (
          <Notice variant="info" className="mt-3">
            What a guest pays is redeemable against food &amp; drink the same day, on property. Redemption is a
            manual / POS step; there is nothing to configure here.
          </Notice>
        )}
      </SectionCard>

      <SectionCard title="Prices" icon={<Tag size={14} />}>
        {/* Season tabs */}
        <div role="tablist" aria-label="Seasons" className="flex gap-2 overflow-x-auto pb-1 mb-4">
          {[{ i: -1, label: 'All year', sub: 'Base prices' }, ...formData.seasons.map((s, i) => ({
            i, label: seasonTabLabel(s, i), sub: s.start_date && s.end_date ? describeDayRange(s.start_date, s.end_date) : 'Set the dates',
          }))].map((t) => {
            const on = tab === t.i;
            const hasErr = t.i >= 0 && (seasonErrors.bySeason[t.i] || []).length > 0;
            return (
              <button
                key={t.i} type="button" role="tab" aria-selected={on} onClick={() => setTab(t.i)}
                className="shrink-0 flex flex-col items-start justify-center h-12 px-3.5 rounded-[10px] cursor-pointer bg-white"
                style={{ border: on ? '2px solid var(--tenant-primary, #005488)' : `1px solid ${hasErr ? 'var(--danger)' : 'var(--line)'}` }}
              >
                <span className="text-[14px] font-semibold" style={{ color: 'var(--ink-900)' }}>{t.label}</span>
                <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>{t.sub}</span>
              </button>
            );
          })}
          <button
            type="button" onClick={onAddSeason}
            className="shrink-0 inline-flex items-center gap-1 h-12 px-3 rounded-[10px] border border-dashed border-[var(--line)] bg-transparent text-[13px] font-semibold cursor-pointer"
            style={{ color: 'var(--tenant-primary, #005488)' }}
          >
            <Plus size={13} /> Add season
          </button>
        </div>

        {season && (
          <div className="rounded-[10px] p-3.5 mb-4" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }} data-testid="season-panel">
            <div className="flex items-center justify-between mb-2.5">
              <span className="flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: 'var(--ink-700)' }}>
                <CalendarRange size={13} /> {seasonTabLabel(season, tab)}
              </span>
              <button type="button" className="btn danger" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => { removeSeason(tab); setTab(-1); }}>
                <X size={12} /> Remove season
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-2.5">
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                Name (EN)
                <input className={text} value={season.label_en} placeholder="High season" onChange={(e) => updateSeason(tab, 'label_en', e.target.value)} />
              </label>
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                Name (ES)
                <input className={text} value={season.label_es} placeholder="Temporada alta" onChange={(e) => updateSeason(tab, 'label_es', e.target.value)} />
              </label>
              {season.every_year ? (
                <>
                  <MonthDayPicker label="From" name={`Season ${tab + 1} start`} value={season.start_date} onChange={(v) => updateSeason(tab, 'start_date', v)} />
                  <MonthDayPicker label="To (inclusive)" name={`Season ${tab + 1} end`} value={season.end_date} onChange={(v) => updateSeason(tab, 'end_date', v)} />
                </>
              ) : (
                <>
                  <DateField label="From" ariaLabel={`Season ${tab + 1} start date`} value={season.start_date} onChange={(v) => updateSeason(tab, 'start_date', v)} />
                  <DateField label="To (inclusive)" ariaLabel={`Season ${tab + 1} end date`} value={season.end_date} onChange={(v) => updateSeason(tab, 'end_date', v)} />
                </>
              )}
            </div>
            <Checkbox
              checked={season.every_year === true}
              onChange={(e) => updateSeasonEveryYear(tab, e.target.checked)}
              aria-label={`Season ${tab + 1} every year`}
              style={{ fontSize: 13, color: 'var(--ink-700)' }}
            >
              <Repeat size={12} /> <span className="font-medium">Every year</span>
            </Checkbox>
            {season.every_year && recurringHints(season).map((h) => (
              <p key={h} className="text-[12px] mt-1" style={{ color: 'var(--ink-500)' }}>{h}</p>
            ))}
            {(seasonErrors.bySeason[tab] || []).map((msg) => (
              <p key={msg} role="alert" className="text-[12px] mt-1.5" style={{ color: 'var(--danger)' }}>{msg}</p>
            ))}
            <p className="text-[12px] mt-2" style={{ color: 'var(--ink-500)' }}>
              Prices below apply on these dates. Leave a price empty to keep the all-year price.
            </p>
          </div>
        )}
        {!season && formData.seasons.length > 0 && (
          <p className="text-[12px] mb-3" style={{ color: 'var(--ink-500)' }}>
            Outside every season these prices apply. Seasons cannot overlap.
          </p>
        )}

        {/* ---------- Per unit ---------- */}
        {unitMode && (
          <div data-testid="pricing-unit">
            <p className="flex flex-wrap items-center gap-2 text-[15px] mb-1" style={{ color: 'var(--ink-900)' }}>
              <span>{sym}</span>
              <input
                type="number" min={0} aria-label={`Price per ${word.singular}`} className={`${money} !w-28`}
                value={formData.unit_price} disabled={!!season} placeholder="e.g. 6000"
                onChange={(e) => updateField('unit_price', e.target.value)}
              />
              <span>per {word.singular} {perDay}, includes</span>
              <input
                type="number" min={0} aria-label="Included guests" className={`${money} !w-20`}
                value={formData.unit_included_pax} disabled={!!season} placeholder="4"
                onChange={(e) => updateField('unit_included_pax', e.target.value)}
              />
              <span>guests, up to</span>
              <input
                type="number" min={1} aria-label="Max guests" className={`${money} !w-20`}
                value={formData.unit_max_pax} disabled={!!season} placeholder="8"
                onChange={(e) => updateField('unit_max_pax', e.target.value)}
              />
              <span>.</span>
            </p>
            {season && (
              <p className="text-[12px] mb-2" style={{ color: 'var(--ink-500)' }}>
                The {word.singular} price is the same all year; a season changes the extra-guest prices below.
              </p>
            )}
            {unitIssues.length > 0 && <Notice variant="warn" className="mt-2">{unitIssues.join(' ')}</Notice>}
            {previewQuote && previewQuote.ok && (
              <p className="text-[12px] mt-1" style={{ color: 'var(--ink-500)' }}>
                Example at max guests ({previewQuote.pax}, all {options[0]?.label_en || 'first type'}): total {sym} {previewQuote.total}
              </p>
            )}

            <div className="overflow-x-auto mt-4 rounded-[10px]" style={{ border: '1px solid var(--line)' }}>
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: 'var(--surface-2)', color: 'var(--ink-500)' }}>
                    <th className={th}>Extra guest</th>
                    <th className={th}>Price each</th>
                  </tr>
                </thead>
                <tbody>
                  {options.map((o, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                      <td className={`${td} text-[14px] pt-3`} style={{ color: 'var(--ink-900)' }}>{optLabel(o, i)}</td>
                      <td className={td}>
                        {priceInput({ label: `${optLabel(o, i)} extra guest price`, base: o.extra_guest_price, seasonValue: seasonEntry(o.id)?.extra_guest_price, onBase: (v) => updateBookingOption(i, 'extra_guest_price', v), onSeason: (v) => updateSeasonPrice(tab, o.id, 'extra_guest_price', '', v) })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {formData.resources.length > 0 && !season && (
              <div className="mt-4">
                <Checkbox checked={perUnitOpen} onChange={(e) => togglePerUnit(e.target.checked)} style={{ fontSize: 14, color: 'var(--ink-900)' }}>
                  Different price per {word.singular}
                </Checkbox>
                {perUnitOpen && (
                  <div className="overflow-x-auto mt-2 rounded-[10px]" style={{ border: '1px solid var(--line)' }}>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr style={{ background: 'var(--surface-2)', color: 'var(--ink-500)' }}>
                          <th className={th}>{capitalize(word.singular)}</th>
                          <th className={th}>Price ({sym})</th>
                          <th className={th}>Includes</th>
                          <th className={th}>Up to</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.resources.map((r, i) => (
                          <tr key={r.key || i} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                            <td className={`${td} text-[14px] pt-3`}>{r.name || r.key}</td>
                            {(['price', 'included_pax', 'max_pax'] as const).map((f) => (
                              <td key={f} className={td}>
                                <input
                                  type="number" min={0} className={money} aria-label={`${r.name} ${f.replace('_', ' ')}`}
                                  value={r[f] ?? ''} onChange={(e) => updateResource(i, f, e.target.value)}
                                  placeholder={(f === 'price' ? formData.unit_price : f === 'included_pax' ? formData.unit_included_pax : formData.unit_max_pax) || '—'}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[12px] px-3 py-2" style={{ color: 'var(--ink-500)' }}>
                      Empty = the price above. The list of {word.plural} comes from Resources.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---------- Per guest: one row per rate group ---------- */}
        {personMode && (
          <div data-testid="pricing-person">
            <div className="overflow-x-auto rounded-[10px]" style={{ border: '1px solid var(--line)' }}>
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: 'var(--surface-2)', color: 'var(--ink-500)' }}>
                    <th className={th}>Rate group</th>
                    <th className={th}>Who qualifies</th>
                    {options.map((o, i) => <th key={i} className={th}>{optLabel(o, i)} ({sym})</th>)}
                    <th className={th}>Max / booking</th>
                    <th className={th}>Max / day</th>
                    <th className={th} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderTop: '1px solid var(--line-subtle)' }}>
                    <td className={`${td} text-[14px] font-semibold pt-3`} style={{ color: 'var(--ink-900)' }}>Default rate</td>
                    <td className={`${td} text-[13px] pt-3`} style={{ color: 'var(--ink-500)' }}>Anyone</td>
                    {options.map((o, i) => (
                      <td key={i} className={td}>
                        {priceInput({ label: `Default rate ${optLabel(o, i)} price`, base: o.price, seasonValue: seasonEntry(o.id)?.price, invalid: invalidPriceIndexes.has(i), onBase: (v) => updateBookingOption(i, 'price', v), onSeason: (v) => updateSeasonPrice(tab, o.id, 'price', '', v) })}
                      </td>
                    ))}
                    <td className={`${td} text-[13px] pt-3`} style={{ color: 'var(--ink-400)' }}>—</td>
                    <td className={`${td} text-[13px] pt-3`} style={{ color: 'var(--ink-400)' }}>—</td>
                    <td />
                  </tr>
                  {groups.map((g, gi) => {
                    const gid = g.id.trim();
                    const name = g.label_en || g.id || `Group ${gi + 1}`;
                    const needsMore = g.eligibility_type === 'document' || g.eligibility_type === 'role' || g.eligibility_type === 'membership';
                    return (
                      <React.Fragment key={gi}>
                        <tr style={{ borderTop: '1px solid var(--line-subtle)' }} data-testid={`rate-group-${gi}`}>
                          <td className={td}>
                            <input
                              className={`${text} min-w-[120px]`} aria-label={`Rate group ${gi + 1} name`} value={g.label_en} placeholder="e.g. Dominican"
                              onChange={(e) => {
                                const v = e.target.value;
                                updateRateGroup(gi, g.id_auto || !g.id ? { label_en: v, id: slugifyGroupId(v), id_auto: true } : { label_en: v });
                              }}
                            />
                          </td>
                          <td className={td}>
                            <select
                              className={`${text} min-w-[150px]`} aria-label={`${name} who qualifies`} value={g.eligibility_type}
                              onChange={(e) => updateRateGroup(gi, { eligibility_type: e.target.value as RateGroupEligibilityType })}
                            >
                              {(Object.keys(ELIGIBILITY_LABELS) as RateGroupEligibilityType[]).map((t) => (
                                <option key={t} value={t}>{ELIGIBILITY_LABELS[t]}</option>
                              ))}
                            </select>
                          </td>
                          {options.map((o, i) => (
                            <td key={i} className={td}>
                              {priceInput({ label: `${name} ${optLabel(o, i)} price`, base: o.prices_by_group[gid] ?? '', seasonValue: seasonEntry(o.id)?.prices_by_group?.[gid], onBase: (v) => updateOptionGroupPrice(i, gid, v), onSeason: (v) => updateSeasonPrice(tab, o.id, 'prices_by_group', gid, v) })}
                            </td>
                          ))}
                          <td className={td}>
                            <input type="number" min={0} className={money} aria-label={`${name} max per booking`} value={g.max_qty_per_booking} placeholder="—" onChange={(e) => updateRateGroup(gi, { max_qty_per_booking: e.target.value })} />
                          </td>
                          <td className={td}>
                            <input type="number" min={0} className={money} aria-label={`${name} max per day`} value={g.max_qty_per_day} placeholder="—" onChange={(e) => updateRateGroup(gi, { max_qty_per_day: e.target.value })} />
                          </td>
                          <td className={td}>
                            <button
                              type="button" aria-label={`Remove rate group ${name}`} onClick={() => removeRateGroup(gi)}
                              className="w-10 h-10 inline-flex items-center justify-center rounded-[8px] border-0 bg-transparent cursor-pointer" style={{ color: 'var(--ink-400)' }}
                            >
                              <X size={15} />
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={options.length + 5} className="px-2 pb-2.5">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px]" style={{ color: 'var(--ink-500)' }}>
                              <label className="inline-flex items-center gap-1.5">
                                ES
                                <input className="h-8 rounded-[6px] border border-solid border-[var(--line)] bg-white px-2 text-[13px]" aria-label={`${name} name in Spanish`} value={g.label_es} placeholder="Dominicano" onChange={(e) => updateRateGroup(gi, { label_es: e.target.value })} />
                              </label>
                              <label className="inline-flex items-center gap-1.5">
                                ID
                                <input className="h-8 w-28 rounded-[6px] border border-solid border-[var(--line)] bg-white px-2 text-[13px]" aria-label={`${name} id`} value={g.id} onChange={(e) => updateRateGroup(gi, { id: e.target.value, id_auto: false })} />
                              </label>
                              {g.eligibility_type === 'document' && (
                                <label className="inline-flex items-center gap-1.5">
                                  Document
                                  <input className="h-8 w-28 rounded-[6px] border border-solid border-[var(--line)] bg-white px-2 text-[13px]" aria-label={`${name} document`} value={g.document} onChange={(e) => updateRateGroup(gi, { document: e.target.value })} />
                                </label>
                              )}
                              {needsMore && g.eligibility_type !== 'document' && (() => {
                                const selected = parseRoles(g.roles);
                                const known = new Set(userTypes.map((u) => String(u.id)));
                                const extra = selected.filter((r) => !known.has(r));
                                return (
                                  <span className="inline-flex flex-wrap items-center gap-1.5" role="group" aria-label={`Roles for ${name}`}>
                                    {g.eligibility_type === 'membership' ? 'Only these roles (optional):' : 'Roles:'}
                                    {[...userTypes.map((u) => ({ id: String(u.id), name: u.name })), ...extra.map((r) => ({ id: r, name: r }))].map((role) => {
                                      const on = selected.includes(role.id);
                                      return (
                                        <button
                                          key={role.id} type="button" aria-pressed={on}
                                          onClick={() => updateRateGroup(gi, { roles: toggleRole(g.roles, role.id) })}
                                          className="rounded-full px-2.5 h-7 text-[12px] cursor-pointer"
                                          style={on
                                            ? { background: 'var(--ink-900)', color: '#fff', border: '1px solid var(--ink-900)' }
                                            : { background: '#fff', border: '1px solid var(--line)', color: 'var(--ink-700)' }}
                                        >
                                          {role.name}
                                        </button>
                                      );
                                    })}
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" className="btn ghost" onClick={addRateGroup}><Plus size={14} /> Add rate group</button>
            </div>
            <p className="text-[12px] mt-2" style={{ color: 'var(--ink-500)' }}>
              One row per rate group. The guest pays the lowest rate they qualify for; an empty price means that
              group can’t buy that guest type.
            </p>
            {groupIssues.length > 0 && <Notice variant="warn" className="mt-2">{groupIssues.join(' ')}</Notice>}
          </div>
        )}

        {/* ---------- Per event size ---------- */}
        {eventMode && (
          <div data-testid="pricing-event">
            <ServiceEventPricingMatrix
              options={options.map((o, index) => ({
                index,
                id: o.id,
                label: optLabel(o, index),
                pricesByEventSize: season ? (seasonEntry(o.id)?.prices_by_event_size ?? {}) : o.prices_by_event_size,
              }))}
              onCellChange={(index, size, value) => (season
                ? updateSeasonPrice(tab, options[index].id, 'prices_by_event_size', size, value)
                : updateOptionEventPrice(index, size, value))}
            />
            <p className="text-[12px] mt-2" style={{ color: 'var(--ink-500)' }}>
              The band is picked from the booking’s total guests and charged once per booking.
              {season ? ' Empty cells keep the all-year price.' : ''}
            </p>
          </div>
        )}
      </SectionCard>

      <SectionCard title={`Guest types (${options.length})`} icon={<Users size={14} />}>
        <p className="text-[12px] mb-3" style={{ color: 'var(--ink-500)' }}>
          {options.length === 0
            ? 'No guest types yet: this service is free to book. Add one to make it paid.'
            : 'Who can be booked and how many per booking. Their prices are set above.'}
        </p>
        {options.map((o, i) => (
          <div key={i} className="rounded-[10px] p-3 mb-2" style={{ border: '1px solid var(--line)', background: 'var(--surface)' }} data-testid={`guest-type-${i}`}>
            <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_100px_70px_70px_40px] gap-2 items-end">
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                Name (EN)
                <input className={text} value={o.label_en} placeholder="Adult" onChange={(e) => updateBookingOption(i, 'label_en', e.target.value)} />
              </label>
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                Name (ES)
                <input className={text} value={o.label_es} placeholder="Adulto" onChange={(e) => updateBookingOption(i, 'label_es', e.target.value)} />
              </label>
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                ID
                <input className={text} value={o.id} placeholder="adult" onChange={(e) => updateBookingOption(i, 'id', e.target.value)} />
              </label>
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                Min
                <input className={text} type="number" value={o.min_amount} placeholder="1" onChange={(e) => updateBookingOption(i, 'min_amount', e.target.value)} />
              </label>
              <label className="text-[12px] flex flex-col gap-1" style={{ color: 'var(--ink-500)' }}>
                Max
                <input className={text} type="number" value={o.max_amount} placeholder="10" onChange={(e) => updateBookingOption(i, 'max_amount', e.target.value)} />
              </label>
              <button
                type="button" aria-label={`Remove guest type ${optLabel(o, i)}`} onClick={() => removeBookingOption(i)}
                className="w-10 h-10 inline-flex items-center justify-center rounded-[8px] border-0 bg-transparent cursor-pointer" style={{ color: 'var(--ink-400)' }}
              >
                <X size={15} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <input className={text} aria-label={`${optLabel(o, i)} short description (EN)`} value={o.short_desc_en} placeholder="Short description (EN), e.g. 13 and older" onChange={(e) => updateBookingOption(i, 'short_desc_en', e.target.value)} />
              <input className={text} aria-label={`${optLabel(o, i)} short description (ES)`} value={o.short_desc_es} placeholder="Descripción corta (ES), p. ej. 13 años o más" onChange={(e) => updateBookingOption(i, 'short_desc_es', e.target.value)} />
            </div>
          </div>
        ))}
        <button type="button" className="btn ghost" onClick={addGuestType}><Plus size={14} /> Add guest type</button>
      </SectionCard>

      {personMode && (
        <details className="mb-4 rounded-[12px] bg-white" style={{ border: '1px solid var(--line)' }} data-testid="pricing-advanced">
          <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-[13px] font-semibold list-none" style={{ color: 'var(--ink-900)' }}>
            <ChevronDown size={14} />
            Advanced · role prices and guest groups (older model)
            {(activeRoleIds.length > 0 || formData.tier_groups.length > 0) && (
              <span className="text-[11px] font-normal" style={{ color: 'var(--ink-500)' }}>
                · {activeRoleIds.length} role{activeRoleIds.length === 1 ? '' : 's'}, {formData.tier_groups.length} group{formData.tier_groups.length === 1 ? '' : 's'}
              </span>
            )}
          </summary>
          <div className="px-4 pb-4">
            <p className="text-[12px] mb-3" style={{ color: 'var(--ink-500)' }}>
              Kept for services that still use them. Rate groups win: a guest type with a rate-group price ignores
              its role prices.
            </p>
            <ServicePricingMatrix
              options={options.map((opt, index) => ({
                index, id: opt.id, label: opt.label_en || opt.label_es, flatPrice: opt.price, pricesByRole: opt.prices_by_role,
              }))}
              activeRoleIds={activeRoleIds}
              defaultUserTypeId={formData.pricing_default_user_type_id}
              roleCaps={formData.pricing_roles}
              onCellChange={updateOptionRolePrice}
              onAddRole={addRoleColumn}
              onRemoveRole={removeRoleColumn}
              onDefaultUserTypeIdChange={updateDefaultUserTypeId}
              onRoleCapChange={updateRoleCap}
            />
            {activeRoleIds.length > 0 && formData.seasons.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--ink-700)' }}>Role prices per season</h4>
                <ServiceSeasonsEditor
                  seasons={formData.seasons}
                  options={options.filter((o) => o.id).map((o) => ({ id: o.id, label: o.label_en || o.label_es || o.id, flatPrice: o.price }))}
                  scalarField={null}
                  roleColumns={activeRoleIds.map((id) => ({ id, label: userTypes.find((ut) => String(ut.id) === id)?.name || `Role #${id}` }))}
                  errors={seasonErrors}
                  onAdd={addSeason}
                  onRemove={removeSeason}
                  onChange={updateSeason}
                  onEveryYearChange={updateSeasonEveryYear}
                  onPriceChange={updateSeasonPrice}
                />
              </div>
            )}
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--line)' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--ink-700)' }}>
                Guest groups{formData.tier_groups.length ? ` (${formData.tier_groups.length})` : ''}
              </h4>
              <ServiceTierGroupsEditor
                groups={formData.tier_groups}
                options={options.filter((opt) => opt.id).map((opt) => ({ id: opt.id, label: opt.label_en || opt.label_es || opt.id }))}
                onAdd={addTierGroup}
                onRemove={removeTierGroup}
                onChange={updateTierGroup}
                onToggleTier={toggleTierGroupOption}
              />
            </div>
          </div>
        </details>
      )}
    </>
  );
}

export default PricingSection;
